<template>
  <div class="space-y-6">
    <!-- En-tête avec bouton d'ajout -->
    <div class="flex justify-between items-center">
      <div>
        <h2 class="text-2xl font-extrabold text-blue-900 dark:text-gray-100">Gestion des Gammes</h2>
        <p class="text-gray-600 dark:text-gray-400 mt-1">Organisez et gérez vos gammes de produits</p>
      </div>
      <button 
        @click="openModal"
        class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-all duration-200 flex items-center shadow-sm hover:shadow-md"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        Ajouter une Gamme
      </button>
    </div>

    <!-- Tableau des gammes -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Nom (FR)</span>
                </div>
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Nom (AR)</span>
                </div>
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Description
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Date</span>
                </div>
              </th>
              <th class="px-6 py-4 text-right text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
            <tr 
              v-for="gamme in paginatedGammes" 
              :key="gamme.id" 
              class="hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors duration-150 group"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                    {{ getGammeInitials(gamme.name) }}
                  </div>
                  <div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ gamme.name }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100" dir="rtl">{{ gamme.name_ar }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-600 dark:text-gray-300 max-w-xs">
                  {{ gamme.description || 'Aucune description' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center space-x-2">
                  <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <div class="text-sm text-gray-500 dark:text-gray-400 font-medium">{{ formatDate(gamme.created_at) }}</div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex justify-end space-x-1">
                  <button 
                    @click="editGamme(gamme)"
                    class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 group/btn"
                    title="Modifier"
                  >
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                  </button>
                  <button 
                    @click="deleteGamme(gamme.id)"
                    class="p-2 text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 rounded-lg transition-all duration-200 group/btn"
                    title="Supprimer"
                  >
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- État de chargement -->
      <div v-if="loading" class="p-8 text-center">
        <div class="inline-flex items-center space-x-3">
          <svg class="animate-spin h-6 w-6 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-600 dark:text-gray-400 font-medium">Chargement des gammes...</span>
        </div>
      </div>

      <!-- Message vide -->
      <div v-if="!loading && gammes.length === 0" class="p-12 text-center">
        <div class="max-w-sm mx-auto">
          <div class="h-20 w-20 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="h-10 w-10 text-blue-900 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-8V4a1 1 0 00-1-1h-2a1 1 0 00-1 1v1M9 7h6"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Aucune gamme trouvée</h3>
          <p class="text-gray-500 dark:text-gray-400 mb-4">Commencez par créer votre première gamme de produits.</p>
          <button 
            @click="openModal"
            class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors inline-flex items-center"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            Créer une gamme
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && gammes.length > 0" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
            <span>Total: </span>
            <span class="font-semibold text-blue-900 dark:text-blue-300">{{ totalItems }}</span>
            <span>gammes</span>
          </div>

          <div class="flex items-center space-x-2">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-700 dark:text-gray-300">Afficher :</span>
              <select 
                v-model="itemsPerPage" 
                @change="currentPage = 1"
                class="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-900"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>

            <div class="flex items-center space-x-1">
              <button
                @click="currentPage = 1"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Première page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page précédente"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <div class="flex items-center space-x-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="currentPage = page"
                  :class="[
                    'px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200',
                    currentPage === page 
                      ? 'bg-blue-900 text-white shadow-sm' 
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  ]"
                >
                  {{ page }}
                </button>
                
                <span v-if="hasMorePages" class="px-2 text-gray-500 dark:text-gray-400">...</span>
              </div>

              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page suivante"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage = totalPages"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Dernière page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour ajouter/modifier une gamme -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-all duration-300">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl transform transition-all duration-300 scale-95 border border-gray-200 dark:border-gray-600">
        <!-- En-tête de la modal -->
        <div class="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 rounded-t-2xl">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-8V4a1 1 0 00-1-1h-2a1 1 0 00-1 1v1M9 7h6"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">
                  {{ isEditing ? 'Modifier la Gamme' : 'Ajouter une Gamme' }}
                </h3>
                <p class="text-blue-100 text-sm">
                  {{ isEditing ? 'Mettez à jour les informations de la gamme' : 'Créez une nouvelle gamme de produits' }}
                </p>
              </div>
            </div>
            <button
              @click="closeModal"
              class="h-8 w-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200 group"
            >
              <svg class="h-5 w-5 text-white group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenu de la modal -->
        <div class="p-6">
          <form @submit.prevent="submitForm">
            <div class="space-y-6">
              <!-- Section Informations de base -->
              <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Informations de la gamme
                </h4>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Nom français -->
                  <div>
                    <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nom français *
                    </label>
                    <input
                      type="text"
                      id="name"
                      v-model="form.name"
                      required
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400"
                      placeholder="Ex: Électronique, Vêtements..."
                      :disabled="submitting"
                    >
                  </div>
                  
                  <!-- Nom arabe -->
                  <div>
                    <label for="name_ar" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nom arabe *
                    </label>
                    <input
                      type="text"
                      id="name_ar"
                      v-model="form.name_ar"
                      required
                      dir="rtl"
                      class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 placeholder-gray-400 text-right"
                      placeholder="أدخل الاسم بالعربية"
                      :disabled="submitting"
                    >
                  </div>
                </div>
                
                <!-- Description -->
                <div class="mt-4">
                  <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    v-model="form.description"
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 resize-none placeholder-gray-400"
                    placeholder="Décrivez cette gamme de produits (optionnel)"
                    :disabled="submitting"
                  ></textarea>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Caractères restants: {{ 255 - (form.description?.length || 0) }}
                  </p>
                </div>
              </div>

              <!-- Indicateur de progression (pour l'édition) -->
              <div v-if="isEditing" class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <div class="flex items-center space-x-3">
                  <svg class="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div>
                    <p class="text-sm font-medium text-blue-800 dark:text-blue-300">Modification en cours</p>
                    <p class="text-xs text-blue-600 dark:text-blue-400">Les modifications seront appliquées immédiatement après validation.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Actions de la modal -->
            <div class="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                @click="closeModal"
                class="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 font-medium disabled:opacity-50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
                :disabled="submitting"
              >
                Annuler
              </button>
              <button
                type="submit"
                class="px-6 py-3 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl hover:from-blue-800 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                :disabled="submitting"
              >
                <svg v-if="submitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ submitting ? 'Traitement...' : (isEditing ? 'Mettre à jour' : 'Créer la gamme') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GammesContent',
  data() {
    return {
      gammes: [],
      loading: false,
      submitting: false,
      showModal: false,
      isEditing: false,
      currentGammeId: null,
      form: {
        name: '',
        name_ar: '',
        description: ''
      },
      // Pagination
      currentPage: 1,
      itemsPerPage: 10
    };
  },
  computed: {
    // Calcul des éléments paginés
    paginatedGammes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.gammes.slice(start, end);
    },
    
    // Informations de pagination
    totalItems() {
      return this.gammes.length;
    },
    
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    
    // Pages visibles pour la pagination
    visiblePages() {
      const pages = [];
      const maxVisiblePages = 5;
      
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
      
      // Ajuster si on est près de la fin
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    hasMorePages() {
      return this.visiblePages[this.visiblePages.length - 1] < this.totalPages;
    }
  },
  watch: {
    // Réinitialiser à la première page quand les données changent
    gammes() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.fetchGammes();
  },
  methods: {
    async fetchGammes() {
      this.loading = true;
      try {
        const response = await fetch('/gammes', {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          this.gammes = data.data || [];
        } else {
          this.showError('Erreur lors du chargement des gammes');
        }
      } catch (error) {
        this.showError('Erreur de connexion');
      } finally {
        this.loading = false;
      }
    },

    openModal() {
      this.isEditing = false;
      this.currentGammeId = null;
      this.form = { name: '', name_ar: '', description: '' };
      this.showModal = true;
    },

    editGamme(gamme) {
      this.isEditing = true;
      this.currentGammeId = gamme.id;
      this.form = {
        name: gamme.name,
        name_ar: gamme.name_ar || '',
        description: gamme.description || ''
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.isEditing = false;
      this.currentGammeId = null;
      this.form = { name: '', name_ar: '', description: '' };
      this.submitting = false;
    },

    async submitForm() {
      this.submitting = true;
      try {
        const url = this.isEditing ? `/gammes/${this.currentGammeId}` : '/gammes';
        const method = this.isEditing ? 'PUT' : 'POST';

        // Récupération du token CSRF depuis les meta tags
        const token = this.getCsrfToken();

        const response = await fetch(url, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(this.form)
        });

        const data = await response.json();

        if (response.ok) {
          this.showSuccess(this.isEditing ? 'Gamme modifiée avec succès' : 'Gamme créée avec succès');
          this.closeModal();
          this.fetchGammes();
        } else {
          // Gestion des erreurs de validation
          if (data.errors) {
            const errorMessages = Object.values(data.errors).flat().join(', ');
            this.showError('Erreur de validation: ' + errorMessages);
          } else {
            this.showError(data.message || 'Une erreur est survenue');
          }
        }
      } catch (error) {
        this.showError('Erreur de connexion au serveur');
      } finally {
        this.submitting = false;
      }
    },

    async deleteGamme(id) {
      if (confirm('Êtes-vous sûr de vouloir supprimer cette gamme ?')) {
        try {
          const token = this.getCsrfToken();
          
          const response = await fetch(`/gammes/${id}`, {
            method: 'DELETE',
            headers: {
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            this.showSuccess('Gamme supprimée avec succès');
            this.fetchGammes();
          } else {
            const data = await response.json();
            this.showError(data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          this.showError('Erreur lors de la suppression');
        }
      }
    },

    // Méthodes d'affichage améliorées
    getGammeInitials(name) {
      if (!name) return '??';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },

    getCsrfToken() {
      // Méthode 1: Récupérer depuis la meta tag
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }

      // Méthode 2: Récupérer depuis les cookies
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (cookieValue) {
        return decodeURIComponent(cookieValue);
      }

      // Méthode 3: Utiliser le token fourni par Laravel
      if (window.Laravel && window.Laravel.csrfToken) {
        return window.Laravel.csrfToken;
      }

      return '';
    },

    showSuccess(message) {
      // Vous pouvez utiliser une librairie de notifications ou un simple alert
      alert('Succès: ' + message);
      // Ou implémenter un système de toast
      // this.$toast.success(message);
    },

    showError(message) {
      alert('Erreur: ' + message);
      // Ou implémenter un système de toast
      // this.$toast.error(message);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>